import { supabase } from '@/lib/supabase';
import type { PlaceData } from '@/types/location';

export type RepairShop = {
  id: string;
  user_id: string;
  place_id?: string | null;
  name?: string | null;
  formatted_address?: string | null;
  phones?: string[]; // phones array
  website?: string | null;
  lat?: number | null;
  lng?: number | null;
  mechanic_name?: string | null;
  registration_number?: string | null;
  bank_accounts?: string[] | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
};

type CarRepairShopLink = {
  car_id: string;
  repair_shop_id: string;
  added_by?: string | null;
  created_at: string;
};

export async function getRepairShops(userId: string): Promise<RepairShop[]> {
  // Legacy user-scoped API (kept for backward compatibility)
  const { data, error } = await supabase.from('repair_shops').select('*').eq('user_id', userId).order('name', { ascending: true });

  if (error) {
    console.error('Error fetching repair shops:', error);
    throw error;
  }
  return data as RepairShop[];
}

export async function getRepairShopsForCar(carId: string): Promise<RepairShop[]> {
  const { data, error } = await supabase
    .from('car_repair_shops')
    .select('repair_shops(*)')
    .eq('car_id', carId);

  if (error) {
    console.error('Error fetching car repair shops:', error);
    throw error;
  }

  const shops = (data || [])
    .map((row: any) => row.repair_shops as RepairShop | null)
    .filter(Boolean) as RepairShop[];

  // Client-side sort for stable UX (PostgREST order across nested selects is awkward)
  shops.sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' }));
  return shops;
}

export async function linkRepairShopToCar(params: { carId: string; repairShopId: string; addedBy?: string | null }): Promise<void> {
  // Select→insert (not upsert): ON CONFLICT DO UPDATE needs UPDATE RLS; re-saving an
  // already-linked shop used to fail with 42501 when only INSERT/SELECT/DELETE existed.
  const { data: existingLink, error: existingLinkError } = await supabase
    .from('car_repair_shops')
    .select('car_id')
    .eq('car_id', params.carId)
    .eq('repair_shop_id', params.repairShopId)
    .maybeSingle();

  if (existingLinkError) {
    console.error('Error checking car repair shop link:', existingLinkError);
    throw existingLinkError;
  }

  if (existingLink) {
    return;
  }

  const { error } = await supabase
    .from('car_repair_shops')
    .insert({
      car_id: params.carId,
      repair_shop_id: params.repairShopId,
      added_by: params.addedBy ?? null,
    } satisfies Partial<CarRepairShopLink>);

  if (error) {
    console.error('Error linking repair shop to car:', error);
    throw error;
  }
}

export async function unlinkRepairShopFromCar(params: { carId: string; repairShopId: string }): Promise<void> {
  const { error } = await supabase
    .from('car_repair_shops')
    .delete()
    .eq('car_id', params.carId)
    .eq('repair_shop_id', params.repairShopId);

  if (error) {
    console.error('Error unlinking repair shop from car:', error);
    throw error;
  }
}

export async function getRepairShopById(id: string): Promise<RepairShop | null> {
  const { data, error } = await supabase
    .from('repair_shops')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error('Error fetching repair shop by ID:', error);
    throw error;
  }
  return data as RepairShop | null;
}

export async function getRepairShopByPlaceId(placeId: string): Promise<RepairShop | null> {
  const { data, error } = await supabase
    .from('repair_shops')
    .select('*')
    .eq('place_id', placeId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching repair shop by place_id:', error);
    throw error;
  }
  return data as RepairShop | null;
}

export async function upsertRepairShop(userId: string, shopData: Omit<RepairShop, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<RepairShop> {
  // Prefer select→update/insert: PostgREST ON CONFLICT cannot use partial unique indexes
  // (prod has UNIQUE(place_id) WHERE place_id IS NOT NULL, which caused 42P10).
  if (shopData.place_id) {
    const { data: existing, error: findError } = await supabase
      .from('repair_shops')
      .select('id')
      .eq('user_id', userId)
      .eq('place_id', shopData.place_id)
      .maybeSingle();

    if (findError) {
      console.error('Error finding repair shop:', findError);
      throw findError;
    }

    if (existing?.id) {
      const { data, error } = await supabase
        .from('repair_shops')
        .update({ ...shopData, user_id: userId })
        .eq('id', existing.id)
        .select()
        .single();
      if (error) {
        console.error('Error updating repair shop:', error);
        throw error;
      }
      return data as RepairShop;
    }
  }

  const { data, error } = await supabase
    .from('repair_shops')
    .insert({ ...shopData, user_id: userId })
    .select()
    .single();

  if (error) {
    console.error('Error inserting repair shop:', error);
    throw error;
  }
  return data as RepairShop;
}

export async function saveRepairShopForCar(params: {
  userId: string;
  carId: string;
  shopData: Omit<RepairShop, 'id' | 'user_id' | 'created_at' | 'updated_at'>;
}): Promise<RepairShop> {
  const shop = await upsertRepairShop(params.userId, params.shopData);
  await linkRepairShopToCar({ carId: params.carId, repairShopId: shop.id, addedBy: params.userId });
  return shop;
}

export async function createRepairShop(userId: string, shopData: Omit<RepairShop, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<RepairShop> {
  // Legacy function - use upsertRepairShop instead
  return upsertRepairShop(userId, shopData);
}

export async function updateRepairShop(id: string, shopData: Partial<Omit<RepairShop, 'id' | 'user_id' | 'created_at' | 'updated_at'>>): Promise<RepairShop> {
  const { data, error } = await supabase
    .from('repair_shops')
    .update(shopData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating repair shop:', error);
    throw error;
  }
  return data as RepairShop;
}

export async function deleteRepairShop(id: string): Promise<void> {
  const { error } = await supabase
    .from('repair_shops')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting repair shop:', error);
    throw error;
  }
}

/**
 * Upsert a repair shop from PlaceData (used when user selects a place)
 * Silently handles missing table errors (42P01) for graceful degradation
 */
export async function upsertRepairShopFromPlace(userId: string, place: PlaceData): Promise<void> {
  if (!place.place_id) {
    return; // Can't save without place_id
  }

  try {
    await upsertRepairShop(userId, {
      place_id: place.place_id,
      name: place.name || null,
      formatted_address: place.formatted_address || null,
      phones: place.phones || [],
      website: place.website || null,
      lat: place.lat || null,
      lng: place.lng || null,
    });
  } catch (error: any) {
    // Silently handle missing table (migration not run yet)
    if (error?.code === '42P01') {
      console.warn('[repairShops] Table does not exist yet, skipping save');
      return;
    }
    // Re-throw other errors
    throw error;
  }
}

